// package imports
import { useState } from 'react';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Theme as MuiTheme } from '@mui/material/styles';

// local imports
import { ObsStyles } from '../../StyledComponents';
import { useObservationStore } from 'components/agility-pageModules/observation/stores/observationStore';
import { useSessionStorage } from 'services/hooks/useSessionStorage';
import { useAddObservationEventNotes } from '../../../hooks/ObservationEventHooks';
import { logClientException } from 'appinsights/clientAppInsights';
import { default as SavedSnack } from '../../../components/AlertSnack';
import { BadRequestError } from 'services/apiWrapper';
import { isOfflineError } from 'services/errorUtils';

const defaultToastAutoHide = 2500;
const offlineSaveMessage =
  'Offline event notes are queued and retried when online. <br>Be sure to reconnect to the internet to save your data within 24 hours.';

const NOTE_TYPE = {
  POSITIVE: 'Positive',
  DELTA: 'Delta',
};

const EventNote: React.FC<{ theme: MuiTheme }> = ({ theme }) => {
  const styles = ObsStyles(theme);

  const [note, setNote] = useSessionStorage('eventNote', '');
  const [noteType, setNoteType] = useSessionStorage(
    'eventNoteType',
    NOTE_TYPE.POSITIVE,
  );
  const [snackSave, setSnackSave] = useState<{
    message: string | null;
    severity: 'success' | 'warning';
    autoHideDuration: number;
  }>({
    message: null,
    severity: 'success',
    autoHideDuration: defaultToastAutoHide,
  });

  const { observation } = useObservationStore();

  const resetNote = () => {
    setNote('');
    setNoteType(NOTE_TYPE.POSITIVE);
  };

  const afterNoteMutate = () => {
    setSnackSave({
      message: 'Note Saved',
      severity: 'success',
      autoHideDuration: defaultToastAutoHide,
    });
    resetNote();
  };

  const afterMutateFail = (error) => {
    logClientException(error);
    if (isOfflineError(error)) {
      // might be offline, should have been added to offline queue to be retried
      setSnackSave({
        message: offlineSaveMessage,
        severity: 'warning',
        autoHideDuration: defaultToastAutoHide,
      });
      resetNote();
    } else if (error instanceof BadRequestError) {
      setSnackSave({
        message: error.formatMessage(),
        severity: 'warning',
        autoHideDuration: defaultToastAutoHide,
      });
    } else {
      setSnackSave({
        message:
          'An unexpected error occurred saving the Event Note. Please try again.',
        severity: 'warning',
        autoHideDuration: defaultToastAutoHide,
      });
    }
  };

  const addNote = useAddObservationEventNotes(
    observation.event_id,
    afterNoteMutate,
    afterMutateFail,
  );

  const addEventNote = async () => {
    if (!note || note.length === 0) return;
    try {
      await addNote.mutateAsync({
        note,
        note_type: noteType,
        id: observation.event_id,
        session_id: observation.id as string,
        added_by: '',
        added_at: new Date().getTime(),
        is_deleted: false,
      });
    } catch (error) {
      logClientException(error);
      console.error('Error adding note:', error);
    }
  };

  const handleNoteTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNoteType((event.target as HTMLInputElement).value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <FormControl fullWidth size="small">
          <TextField
            placeholder="Type note"
            multiline={true}
            rows={2}
            sx={{ ...styles.select, marginTop: '.75rem' }}
            label="New Event Note"
            id="event-note"
            size="small"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
            disabled={addNote.isPending}
            slotProps={{
              input: { sx: { ...styles.select } },
              htmlInput: { maxLength: 500 },
              inputLabel: { shrink: true }
            }} />
        </FormControl>

        <IconButton
          onClick={addEventNote}
          style={{ marginTop: '.5rem' }}
          disabled={addNote.isPending || note.length === 0}
          aria-label="Add event note"
        >
          <ArrowCircleRightIcon
            sx={{ color: theme.palette.gold.main }}
            fontSize="large"
          />
        </IconButton>
      </Box>
      <FormControl>
        <RadioGroup
          row
          name="note-type"
          defaultValue={NOTE_TYPE.POSITIVE}
          value={noteType}
          onChange={handleNoteTypeChange}
        >
          <FormControlLabel
            sx={{ '& .MuiTypography-root': { fontSize: '.875rem' } }}
            control={<Radio size="small" />}
            value={NOTE_TYPE.POSITIVE}
            label={NOTE_TYPE.POSITIVE}
          />
          <FormControlLabel
            sx={{ '& .MuiTypography-root': { fontSize: '.875rem' } }}
            control={<Radio size="small" />}
            value={NOTE_TYPE.DELTA}
            label={NOTE_TYPE.DELTA}
          />
        </RadioGroup>
      </FormControl>
      <SavedSnack
        message={snackSave.message}
        openSnack={snackSave.message === null ? false : true}
        onClose={() => {
          setSnackSave({
            message: null,
            severity: 'success',
            autoHideDuration: defaultToastAutoHide,
          });
        }}
        severity={snackSave.severity}
        autoHideDuration={snackSave.autoHideDuration}
      />
    </Box>
  );
};

export default EventNote;
